/**
 * mockHelpers  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

/**
 * Generates random number in specified or default range and returns it.
 *
 * Example:
 * randomNumber() // 4
 * randomNumber(0, 99999999999999999999) // 01189998819991197253
 *
 * @param from
 * @param to
 */
export const randomNumber = (from = 1, to = 100) => Math.floor(Math.random() * to) + from;

/**
 * Generates random non-readable string with length in specified or default range
 * and returns it.
 *
 * Example:
 * randomString(5, 18)
 *
 * @param minLength
 * @param maxLength
 */
export const randomString = (minLength = 1, maxLength = 12) =>
	Math.random().toString(20).substr(2, randomNumber(minLength, maxLength));

/**
 * Calls an input function n times with given arguments, returning an array
 * containing the results of those function calls.
 *
 * Example:
 * times(() => 'tap', 3) // ['tap', 'tap', 'tap']
 * times(randomString, 4, 1, 3) // ['z3cb', 'f8j', 'a', 'op7']
 *
 * @param fn
 * @param times
 * @param args
 */
export const times = <
	T extends unknown = unknown,
	F extends (...args: unknown[]) => T = (...args: unknown[]) => T
>(
	fn: F,
	times = 1,
	...args: Parameters<F>
): Array<ReturnType<F>> =>
	Array(times)
		.fill(null)
		.map(() => fn(...args) as ReturnType<F>);

/**
 * Takes array of options and randomly selects one and returns it.
 *
 * Example:
 * randomItem(['hey', 'ola', 'aaa']) // 'aaa'
 * randomItem([null, times(randomNumber, 8, 2, 100)]) // null
 *
 * @param items
 */
export const randomItem = <I extends unknown[]>(items: I): I[number] => items[randomNumber(0, items.length)];
